import { type Placement, usePortalPlacement } from "@/hooks/usePortalPlacement";
import { type ReactNode, useCallback, useId } from "react";
import { createPortal } from "react-dom";

const GAP_PX = 6;

function computePlacement(el: Element | null): Placement {
  if (!el) return { top: 0, left: 0, transform: "translateX(-50%)" };
  const rect = el.getBoundingClientRect();
  const openUp = rect.top > window.innerHeight - rect.bottom || rect.top > 120;
  const left = Math.max(60, Math.min(rect.left + rect.width / 2, window.innerWidth - 60));
  if (openUp) {
    return { top: rect.top - GAP_PX, left, transform: "translate(-50%, -100%)" };
  }
  return { top: rect.bottom + GAP_PX, left, transform: "translateX(-50%)" };
}

export interface PortalTooltipProps {
  label: string;
  children: ReactNode;
}

export function PortalTooltip({ label, children }: PortalTooltipProps) {
  const tooltipId = useId();
  const computePlacementForAnchor = useCallback(
    (anchor: HTMLSpanElement | null) => computePlacement(anchor),
    []
  );
  const { anchorRef, isOpen, coords, open, close } =
    usePortalPlacement<HTMLSpanElement>(computePlacementForAnchor);

  return (
    <>
      <span
        ref={anchorRef}
        className="inline-flex"
        onMouseEnter={open}
        onMouseLeave={close}
        onFocusCapture={open}
        onBlurCapture={close}
      >
        {children}
      </span>
      {isOpen &&
        createPortal(
          <div
            id={tooltipId}
            role="tooltip"
            className="pointer-events-none fixed rounded-md bg-foreground px-2 py-1 text-xs font-medium text-white shadow-lg"
            style={{
              top: coords.top,
              left: coords.left,
              transform: coords.transform,
              zIndex: "var(--z-tooltip)",
            }}
          >
            {label}
          </div>,
          document.body
        )}
    </>
  );
}
